import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getSuperSalesAdmins,
  getSuperSalesAdminDetail,
  getSuperAgentDetail,
} from "../../../redux/actions/profile";
import { loadStart, loadStop } from "../../../redux/actions/loading";
import Flash from "../../../utility/Flash";
import { generateExtSalesInvite } from "../../../utility/axios-token-manager/auth";
import MerchantDetailPanel from "../../shared/MerchantDetailPanel";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

export default function SalesAdmins() {
  const dispatch = useDispatch();
  const admins = useSelector((s) => s.profile.superSalesAdmins || []);
  const adminDetail = useSelector((s) => s.profile.superSalesAdminDetail);
  const agentDetail = useSelector((s) => s.profile.superAgentDetail);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [agentModal, setAgentModal] = useState(null);
  const [loadingAgent, setLoadingAgent] = useState(false);
  const [merchantPanel, setMerchantPanel] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => { dispatch(getSuperSalesAdmins()); }, [dispatch]);

  const filtered = admins.filter((a) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (a.email || "").toLowerCase().includes(q) ||
      (a.fullName || "").toLowerCase().includes(q) ||
      (a.adminRefCode || "").toLowerCase().includes(q)
    );
  });

  const topByAgents = [...admins]
    .sort((a, b) => (b.agentCount || 0) - (a.agentCount || 0))
    .slice(0, 3);
  const totalAgents = admins.reduce((s, a) => s + (a.agentCount || 0), 0);
  const totalMerchants = admins.reduce((s, a) => s + (a.merchantCount || 0), 0);

  const openDetail = async (admin) => {
    setSelected(admin);
    setLoadingDetail(true);
    await dispatch(getSuperSalesAdminDetail(admin._id));
    setLoadingDetail(false);
  };

  const openAgentModal = async (agent) => {
    setAgentModal({ agent, fetched: false });
    setLoadingAgent(true);
    await dispatch(getSuperAgentDetail(agent._id));
    setLoadingAgent(false);
    setAgentModal((prev) => (prev ? { ...prev, fetched: true } : prev));
  };

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Sales Admins</h2>
          <p style={S.sub}>All EX-DSA-EXT-ADMIN users on the platform</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={S.badge}>{admins.length} admins</span>
          <button style={S.inviteBtn} onClick={() => setInviteOpen(true)}>
            <i className="bi bi-person-plus" /> Invite Sales Admin
          </button>
        </div>
      </div>

      <div style={S.statRow}>
        <div style={S.statCard}>
          <i className="bi bi-shield-person-fill" style={{ fontSize: 22, color: PRIMARY }} />
          <div style={S.statNum}>{admins.length}</div>
          <div style={S.statLabel}>Total Sales Admins</div>
        </div>
        <div style={S.statCard}>
          <i className="bi bi-person-badge-fill" style={{ fontSize: 22, color: "#4fb0c6" }} />
          <div style={{ ...S.statNum, color: "#4fb0c6" }}>{totalAgents}</div>
          <div style={S.statLabel}>Total Agents Managed</div>
        </div>
        <div style={S.statCard}>
          <i className="bi bi-shop" style={{ fontSize: 22, color: "#f4a261" }} />
          <div style={{ ...S.statNum, color: "#f4a261" }}>{totalMerchants}</div>
          <div style={S.statLabel}>Total Merchants Referred</div>
        </div>
      </div>

      {topByAgents.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={S.sectionLabel}>
            <i className="bi bi-trophy-fill" style={{ color: PRIMARY, marginRight: 6 }} />
            Top Admins by Agent Count
          </div>
          <div style={S.topRow}>
            {topByAgents.map((a, i) => (
              <div
                key={a._id}
                style={{ ...S.topCard, cursor: "pointer" }}
                onClick={() => openDetail(a)}
              >
                <div style={{ ...S.topRank, background: i === 0 ? "#f4a261" : i === 1 ? "#bbb" : "#c87941" }}>
                  #{i + 1}
                </div>
                <div style={S.topName}>{a.fullName || a.email?.split("@")[0] || "—"}</div>
                <div style={S.topEmail}>{a.email}</div>
                <div style={S.topStats}>
                  <span><strong>{a.agentCount || 0}</strong> agents</span>
                  <span><strong>{a.merchantCount || 0}</strong> merchants</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={S.searchWrap}>
        <i className="bi bi-search" style={S.searchIcon} />
        <input
          style={S.search}
          placeholder="Search by name, email, or ref code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Name / Email", "Ref Code", "Agents", "Merchants", "Joined", ""].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>
                  No admins found
                </td>
              </tr>
            ) : filtered.map((a) => (
              <tr key={a._id} style={S.tr}>
                <td style={S.td}>
                  <div style={{ fontWeight: 600, color: BG, fontSize: 13 }}>{a.fullName || "—"}</div>
                  <div style={{ fontSize: 11, color: "#999" }}>{a.email}</div>
                </td>
                <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12 }}>{a.adminRefCode || "—"}</td>
                <td style={S.td}>
                  <div style={S.countBadge}>{a.agentCount || 0}</div>
                </td>
                <td style={S.td}>
                  <div style={{ ...S.countBadge, background: "rgba(244,162,97,0.12)", color: "#b05a00" }}>
                    {a.merchantCount || 0}
                  </div>
                </td>
                <td style={{ ...S.td, fontSize: 12, color: "#888" }}>
                  {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "—"}
                </td>
                <td style={S.td}>
                  <button style={S.viewBtn} onClick={() => openDetail(a)}>
                    <i className="bi bi-eye" /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Admin detail panel */}
      {selected && (
        <AdminDetailPanel
          admin={selected}
          detail={adminDetail}
          loading={loadingDetail}
          onClose={() => setSelected(null)}
          onViewAgent={openAgentModal}
          onViewMerchant={setMerchantPanel}
        />
      )}

      {/* Agent detail modal — z-index above admin panel */}
      {agentModal && (
        <AgentDetailModal
          agent={agentModal.agent}
          detail={agentModal.fetched ? agentDetail : null}
          loading={loadingAgent}
          onClose={() => setAgentModal(null)}
          onViewMerchant={setMerchantPanel}
        />
      )}

      {/* Merchant detail panel — topmost layer */}
      {merchantPanel && (
        <MerchantDetailPanel
          merchant={merchantPanel}
          onClose={() => setMerchantPanel(null)}
          canViewSensitive
        />
      )}

      {/* Invite sales admin modal */}
      {inviteOpen && <InviteAdminModal onClose={() => setInviteOpen(false)} />}
    </div>
  );
}

// ── Invite sales admin modal ───────────────────────────────────────────────────

function InviteAdminModal({ onClose }) {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return Flash("error", "Please enter an email address", "", 3000);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) return Flash("error", "Invalid email address", "", 3000);

    try {
      setSending(true);
      dispatch(loadStart());
      const result = await generateExtSalesInvite(trimmed);
      dispatch(loadStop());
      setSending(false);
      if (result.success) {
        Flash("success", `Invite sent to ${trimmed}`, "", 4000);
        setSent(true);
        setEmail("");
      } else {
        Flash("error", result.message || "Failed to send invite", "", 5000);
      }
    } catch (error) {
      dispatch(loadStop());
      setSending(false);
      Flash("error", "Server error", "", 5000);
    }
  };

  return (
    <div style={{ ...S.overlay, alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={S.inviteModal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: BG }}>Invite Sales Admin</h3>
          <button onClick={onClose} style={S.closeBtn}><i className="bi bi-x-lg" /></button>
        </div>
        <p style={{ fontSize: 13, color: "#888", margin: "4px 0 20px" }}>
          Enter the email address of the person you want to set up as an External Sales Admin.
          They will receive a one-time setup link valid for 24 hours.
        </p>

        <label style={S.inviteLabel}>Email Address</label>
        <input
          type="email"
          style={S.inviteInput}
          placeholder="e.g. jane@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          disabled={sending}
          autoFocus
        />

        <button style={S.inviteSubmitBtn} onClick={handleSubmit} disabled={sending}>
          {sending ? "Sending…" : "Send Invite Link"}
        </button>

        {sent && (
          <p style={{ color: PRIMARY, textAlign: "center", fontSize: 13, marginTop: 12, fontWeight: 600 }}>
            <i className="bi bi-check-circle-fill" /> A setup link has been emailed. It expires in 24 hours and can only be used once.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Admin detail side panel ────────────────────────────────────────────────────

function AdminDetailPanel({ admin, detail, loading, onClose, onViewAgent, onViewMerchant }) {
  const [expandedAgent, setExpandedAgent] = useState(null);

  const agents = detail?.agents || [];
  const merchants = detail?.merchants || [];

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: BG }}>{admin.fullName || "—"}</h3>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
              {admin.email} · <span style={{ fontFamily: "monospace" }}>{admin.adminRefCode}</span>
            </div>
          </div>
          <button onClick={onClose} style={S.closeBtn}><i className="bi bi-x-lg" /></button>
        </div>

        <div style={S.modalBody}>
          {loading ? (
            <div style={S.centered}><div style={S.spinner} /></div>
          ) : (
            <>
              <div style={S.infoGrid}>
                {[
                  ["Agents", admin.agentCount ?? agents.length],
                  ["Merchants", admin.merchantCount ?? merchants.length],
                  ["Ref Code", admin.adminRefCode || "—"],
                  ["Joined", admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : "—"],
                ].map(([k, v]) => (
                  <div key={k} style={S.infoItem}>
                    <div style={S.infoKey}>{k}</div>
                    <div style={S.infoVal}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Agents */}
              <div style={S.sectionLabel}>
                <i className="bi bi-person-badge-fill" style={{ color: "#4fb0c6", marginRight: 6 }} />
                Agents ({agents.length})
              </div>

              {agents.length === 0 ? (
                <div style={S.empty}>No agents under this admin</div>
              ) : (
                <div style={{ marginBottom: 20 }}>
                  {agents.map((agent) => (
                    <AgentRow
                      key={agent._id}
                      agent={agent}
                      expanded={expandedAgent === agent._id}
                      onToggle={() => setExpandedAgent(expandedAgent === agent._id ? null : agent._id)}
                      onViewMore={() => onViewAgent(agent)}
                      onViewMerchant={onViewMerchant}
                    />
                  ))}
                </div>
              )}

              {/* Direct merchants */}
              {merchants.length > 0 && (
                <>
                  <div style={S.sectionLabel}>
                    <i className="bi bi-shop" style={{ color: "#f4a261", marginRight: 6 }} />
                    Direct Merchants ({merchants.length})
                  </div>
                  <div style={S.miniTable}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr>
                          {["Store", "Email", "Plan", ""].map((h) => (
                            <th key={h} style={S.miniTh}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {merchants.map((m, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                            <td style={S.miniTd}>{m.storeInfo?.storeName || "—"}</td>
                            <td style={S.miniTd}>{m.email}</td>
                            <td style={S.miniTd}>{m.plan || "Free"}</td>
                            <td style={S.miniTd}>
                              <button style={S.viewMoreBtn} onClick={() => onViewMerchant(m)}>
                                View more
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Collapsible agent row ─────────────────────────────────────────────────────

function AgentRow({ agent, expanded, onToggle, onViewMore, onViewMerchant }) {
  const merchants = agent.merchants || [];
  return (
    <div style={S.agentCard}>
      <div style={S.agentCardHeader} onClick={onToggle}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: BG }}>{agent.fullName || "—"}</div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {agent.email} · <span style={{ fontFamily: "monospace" }}>{agent.agentCode}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ ...S.countBadge, fontSize: 11 }}>{merchants.length} merchants</span>
          <button
            style={S.viewMoreBtn}
            onClick={(e) => { e.stopPropagation(); onViewMore(); }}
          >
            View more
          </button>
          <i className={`bi bi-chevron-${expanded ? "up" : "down"}`} style={{ color: "#bbb", fontSize: 12 }} />
        </div>
      </div>

      {expanded && (
        <div style={S.agentMerchants}>
          {merchants.length === 0 ? (
            <div style={{ fontSize: 12, color: "#bbb", padding: "8px 0" }}>No merchants referred by this agent</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Store", "Email", "Plan", ""].map((h) => (
                    <th key={h} style={S.miniTh}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {merchants.map((m, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <td style={S.miniTd}>{m.storeInfo?.storeName || "—"}</td>
                    <td style={S.miniTd}>{m.email}</td>
                    <td style={S.miniTd}>{m.plan || "Free"}</td>
                    <td style={S.miniTd}>
                      <button style={S.viewMoreBtn} onClick={() => onViewMerchant(m)}>
                        View more
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ── Agent detail modal ────────────────────────────────────────────────────────

function AgentDetailModal({ agent, detail, loading, onClose, onViewMerchant }) {
  const merchants = detail?.merchants || agent.merchants || [];

  return (
    <div style={{ ...S.overlay, zIndex: 1100 }} onClick={onClose}>
      <div style={{ ...S.modal, boxShadow: "-4px 0 24px rgba(0,0,0,0.22)" }} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div>
            <div style={{ fontSize: 11, color: "#aaa", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 }}>
              Agent Detail
            </div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: BG }}>{agent.fullName || "—"}</h3>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
              {agent.email} · <span style={{ fontFamily: "monospace" }}>{agent.agentCode}</span>
            </div>
          </div>
          <button onClick={onClose} style={S.closeBtn}><i className="bi bi-x-lg" /></button>
        </div>

        <div style={S.modalBody}>
          {loading ? (
            <div style={S.centered}><div style={S.spinner} /></div>
          ) : (
            <>
              <div style={S.infoGrid}>
                {[
                  ["Status", agent.approve ? "Approved" : "Pending"],
                  ["Lead Agent", agent.isLead ? "Yes" : "No"],
                  ["Merchants", merchants.length],
                  ["Last Login", agent.lastLogin ? new Date(agent.lastLogin).toLocaleDateString() : "Never"],
                  ["Joined", agent.createdAt ? new Date(agent.createdAt).toLocaleDateString() : "—"],
                  ...(detail?.agent?.mobile ? [["Mobile", detail.agent.mobile]] : []),
                  ...(detail?.agent?.address ? [["Address", detail.agent.address]] : []),
                  ...(detail?.totalEarnings != null ? [["Total Earnings", `₦${(detail.totalEarnings / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`]] : []),
                ].map(([k, v]) => (
                  <div key={k} style={S.infoItem}>
                    <div style={S.infoKey}>{k}</div>
                    <div style={S.infoVal}>{v}</div>
                  </div>
                ))}
              </div>

              <div style={S.sectionLabel}>
                <i className="bi bi-shop" style={{ color: "#f4a261", marginRight: 6 }} />
                Merchants Referred ({merchants.length})
              </div>

              {merchants.length === 0 ? (
                <div style={S.empty}>No merchants referred by this agent</div>
              ) : (
                <div style={S.miniTable}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr>
                        {["Store", "Email", "Plan", "Last Login", ""].map((h) => (
                          <th key={h} style={S.miniTh}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {merchants.map((m, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                          <td style={S.miniTd}>{m.storeInfo?.storeName || "—"}</td>
                          <td style={S.miniTd}>{m.email}</td>
                          <td style={S.miniTd}>{m.plan || "Free"}</td>
                          <td style={S.miniTd}>{m.lastLogin ? new Date(m.lastLogin).toLocaleDateString() : "Never"}</td>
                          <td style={S.miniTd}>
                            <button style={S.viewMoreBtn} onClick={() => onViewMerchant(m)}>
                              View more
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {detail?.feedbacks?.length > 0 && (
                <>
                  <div style={{ ...S.sectionLabel, marginTop: 20 }}>
                    <i className="bi bi-chat-left-text" style={{ color: PRIMARY, marginRight: 6 }} />
                    Recent Feedbacks ({detail.feedbacks.length})
                  </div>
                  {detail.feedbacks.slice(0, 5).map((f, i) => (
                    <div key={i} style={S.feedbackCard}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={S.sectorTag}>Sector {f.sector}</span>
                        <span style={{ fontSize: 11, color: "#bbb" }}>
                          {f.date ? new Date(f.date).toLocaleDateString() : "—"}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: 13, color: "#444" }}>{f.report}</p>
                      {f.location && (
                        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                          <i className="bi bi-geo-alt" /> {f.location}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  sub: { fontSize: 13, color: "#888", marginTop: 4 },
  badge: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", border: "1px solid " + PRIMARY, borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700 },
  inviteBtn: { background: PRIMARY, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" },
  inviteModal: { background: "#fff", width: "min(440px, 92vw)", borderRadius: 12, padding: "24px 26px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" },
  inviteLabel: { display: "block", fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 },
  inviteInput: { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 13, outline: "none", boxSizing: "border-box" },
  inviteSubmitBtn: { width: "100%", marginTop: 16, background: PRIMARY, color: "#fff", border: "none", borderRadius: 8, padding: "11px", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  statRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16, marginBottom: 28 },
  statCard: { background: "#fff", borderRadius: 10, padding: "18px 20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", textAlign: "center" },
  statNum: { fontSize: 28, fontWeight: 800, color: PRIMARY, marginTop: 8 },
  statLabel: { fontSize: 12, color: "#888", marginTop: 4 },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, display: "flex", alignItems: "center" },
  topRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 },
  topCard: { background: "#fff", borderRadius: 10, padding: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", position: "relative" },
  topRank: { position: "absolute", top: 12, right: 12, color: "#fff", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 800 },
  topName: { fontSize: 14, fontWeight: 700, color: BG, marginBottom: 2 },
  topEmail: { fontSize: 11, color: "#aaa", marginBottom: 10 },
  topStats: { display: "flex", gap: 14, fontSize: 12, color: "#666" },
  searchWrap: { position: "relative", marginBottom: 16, maxWidth: 400 },
  searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#bbb", fontSize: 14 },
  search: { width: "100%", padding: "9px 12px 9px 36px", borderRadius: 8, border: "1.5px solid #eee", fontSize: 13, outline: "none", boxSizing: "border-box" },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 580 },
  th: { background: BG, color: "#fff", padding: "11px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "11px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  countBadge: { display: "inline-block", background: "rgba(167,204,72,0.12)", color: "#5a8a00", borderRadius: 20, padding: "2px 12px", fontWeight: 700, fontSize: 13 },
  viewBtn: { background: BG, color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 },
  viewMoreBtn: { background: "none", border: "1.5px solid #e0e0e0", color: "#555", borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" },
  modal: { background: "#fff", width: "min(580px, 95vw)", height: "100vh", overflowY: "auto", boxShadow: "-4px 0 20px rgba(0,0,0,0.15)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "20px 24px 16px", borderBottom: "1px solid #f0f0f0", position: "sticky", top: 0, background: "#fff", zIndex: 1 },
  closeBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888", padding: 4 },
  modalBody: { padding: "20px 24px" },
  centered: { display: "flex", justifyContent: "center", padding: 40 },
  spinner: { width: 32, height: 32, borderRadius: "50%", border: "3px solid " + PRIMARY, borderTopColor: "transparent", animation: "spin 0.8s linear infinite" },
  infoGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 },
  infoItem: { background: "#f9f9f9", borderRadius: 8, padding: "10px 12px" },
  infoKey: { fontSize: 11, color: "#aaa", fontWeight: 600, textTransform: "uppercase", marginBottom: 3 },
  infoVal: { fontSize: 14, color: BG, fontWeight: 700 },
  empty: { fontSize: 13, color: "#bbb", padding: "12px 0 20px" },
  agentCard: { border: "1px solid #f0f0f0", borderRadius: 8, marginBottom: 8, overflow: "hidden" },
  agentCardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", cursor: "pointer", background: "#fafafa" },
  agentMerchants: { borderTop: "1px solid #f0f0f0", padding: "10px 14px", background: "#fff" },
  miniTable: { border: "1px solid #f0f0f0", borderRadius: 8, overflowX: "auto", WebkitOverflowScrolling: "touch", marginBottom: 4 },
  miniTh: { background: "#f7f7f7", padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#888" },
  miniTd: { padding: "8px 10px", color: "#555", fontSize: 12 },
  feedbackCard: { background: "#f9f9f9", borderRadius: 8, padding: "12px 14px", marginBottom: 8 },
  sectorTag: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 700 },
};
